import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Ban, FileQuestion, Loader2, MessageSquareQuote, Send, ShoppingCart } from 'lucide-react';
import toast from 'react-hot-toast';
import { AccountingSubNav } from '@/features/accounting/components/AccountingSubNav';
import { getApiErrorMessage } from '@/shared/api/axios';
import { RecordQuoteModal } from '../components/RecordQuoteModal';
import { RfqComparisonTable } from '../components/RfqComparisonTable';
import {
  awardRfqAndRaisePo,
  cancelRfq,
  getRfq,
  listScorecards,
  selectSupplier,
  sendRfq,
} from '../api/procurementRfqApi';
import { RFQ_STATUS_TONE, formatMinor } from '../constants';
import { ROUTES } from '@/routes/paths';
import type { Rfq, RfqComparison, RfqComparisonRow, SupplierScorecard } from '../types';

/**
 * One RFQ: the items that went out, the side-by-side comparison of what came
 * back, and the decision.
 *
 * The comparison rendered here is ALWAYS the server's. GET /:id returns
 * { rfq, comparison } together, and every mutation on this screen
 * (/quotes, /select) returns the recomputed pair as well, so the table is
 * replaced with the server's ranking rather than re-derived in the browser.
 * That is what keeps "who is cheapest / fastest / best value" identical to the
 * figures the awarded purchase order is built from.
 *
 * Selection and awarding are authorised server-side by requirePermission() on
 * the accounting router. Nothing is gated client-side here: this screen's route
 * carries the `accounts` workspace gate, exactly like every other Accounting
 * page, and the accounting permission namespace is not what `actionPermissions`
 * exposes to the browser — gating buttons on it would deny everybody.
 */
export function RfqDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [rfq, setRfq] = useState<Rfq | null>(null);
  const [comparison, setComparison] = useState<RfqComparison | null>(null);
  const [scorecards, setScorecards] = useState<Record<string, SupplierScorecard>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showQuote, setShowQuote] = useState(false);
  const [quoteSupplierId, setQuoteSupplierId] = useState<string | null>(null);
  const [selectingSupplierId, setSelectingSupplierId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** One scorecard request for every bidder, not one per row. */
  const loadScorecards = useCallback(async (loaded: Rfq) => {
    const supplierIds = (loaded.quotes || []).map(quote => quote.supplierId).filter(Boolean);
    if (supplierIds.length === 0) { setScorecards({}); return; }
    try {
      const cards = await listScorecards(supplierIds);
      setScorecards(Object.fromEntries(cards.map(card => [card.supplierId, card])));
    } catch {
      // A missing scorecard is not a reason to fail the screen — the comparison
      // rows carry their own score figures as a fallback.
      setScorecards({});
    }
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getRfq(id);
      setRfq(result.rfq);
      setComparison(result.comparison);
      await loadScorecards(result.rfq);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load this RFQ.'));
      setRfq(null);
      setComparison(null);
    } finally {
      setLoading(false);
    }
  }, [id, loadScorecards]);

  useEffect(() => { load(); }, [load]);

  const applyResult = (result: { rfq: Rfq; comparison: RfqComparison }) => {
    setRfq(result.rfq);
    setComparison(result.comparison);
  };

  const handleSend = async () => {
    if (!rfq) return;
    setBusy(true);
    try {
      const sent = await sendRfq(rfq._id);
      setRfq(sent);
      toast.success(`${sent.rfqNumber || 'RFQ'} sent to suppliers.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not send this RFQ.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSelect = async (row: RfqComparisonRow) => {
    if (!rfq) return;
    setSelectingSupplierId(row.supplierId);
    try {
      applyResult(await selectSupplier(rfq._id, row.supplierId));
      toast.success(`${row.supplierName || 'Supplier'} selected for the purchase order.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not select that supplier.'));
    } finally {
      setSelectingSupplierId(null);
    }
  };

  const handleAward = async () => {
    if (!rfq) return;
    setBusy(true);
    try {
      const { rfq: awarded, purchaseOrderId } = await awardRfqAndRaisePo(rfq._id);
      setRfq(awarded);
      toast.success(`Purchase order raised (${purchaseOrderId.slice(-8)}).`);
      // Reload so the comparison reflects the awarded state the server now holds.
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not award this RFQ.'));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!rfq) return;
    setBusy(true);
    try {
      const cancelled = await cancelRfq(rfq._id);
      setRfq(cancelled);
      toast.success('RFQ cancelled.');
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not cancel this RFQ.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="">
        <AccountingSubNav />
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      </div>
    );
  }

  if (error || !rfq || !comparison) {
    return (
      <div className="">
        <AccountingSubNav />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center space-y-4">
          <div className="h-14 w-14 rounded-xl bg-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-400 flex items-center justify-center mx-auto">
            <FileQuestion className="w-6 h-6" />
          </div>
          <p className="text-sm text-slate-500">{error || 'This RFQ could not be found.'}</p>
          <button
            onClick={() => navigate(ROUTES.accountingV2Rfqs)}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            Back to all RFQs
          </button>
        </div>
      </div>
    );
  }

  // Awarded, cancelled or already converted — the decision can no longer move.
  const locked = rfq.status === 'awarded' || rfq.status === 'cancelled' || Boolean(rfq.purchaseOrderId);
  const canAward = Boolean(rfq.selectedSupplierId) && !locked;

  return (
    <div className="">
      <AccountingSubNav />
      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        <Link to={ROUTES.accountingV2Rfqs} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-indigo-600 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> All requests for quotation
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-sm shrink-0">
              <FileQuestion className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {rfq.rfqNumber || 'RFQ'}
                </h1>
                <span className={`uppercase text-[10px] px-2.5 py-0.5 rounded-full font-bold ${RFQ_STATUS_TONE[rfq.status] || RFQ_STATUS_TONE.draft}`}>
                  {rfq.status}
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {rfq.title || 'Untitled RFQ'}
                {rfq.dueDate && ` — quotes due ${new Date(rfq.dueDate).toLocaleDateString('en-IN')}`}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {rfq.status === 'draft' && (
              <button
                onClick={handleSend}
                disabled={busy}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-indigo-300 disabled:opacity-50 transition-colors"
              >
                <Send className="w-4 h-4" /> Send to suppliers
              </button>
            )}
            {!locked && (
              <button
                onClick={() => { setQuoteSupplierId(null); setShowQuote(true); }}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <MessageSquareQuote className="w-4 h-4" /> Record quote
              </button>
            )}
            {canAward && (
              <button
                onClick={handleAward}
                disabled={busy}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
                Award & raise PO
              </button>
            )}
            {!locked && (
              <button
                onClick={handleCancel}
                disabled={busy}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold border border-slate-200 dark:border-slate-800 text-slate-400 hover:text-rose-600 hover:border-rose-300 disabled:opacity-50 transition-colors"
              >
                <Ban className="w-4 h-4" /> Cancel RFQ
              </button>
            )}
          </div>
        </div>

        {rfq.purchaseOrderId && (
          <div className="flex items-center gap-2 p-4 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-800 dark:text-emerald-300 text-sm shadow-sm">
            <ShoppingCart className="w-5 h-5 shrink-0 text-emerald-600" />
            This RFQ has been converted to a purchase order.
            <Link to={ROUTES.accountingV2P2p} className="font-semibold underline">Open Procure-to-Pay</Link>
          </div>
        )}

        {rfq.selectionReason && (
          <div className="text-xs text-slate-500 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3">
            <span className="font-semibold text-slate-600 dark:text-slate-300">Why this supplier: </span>{rfq.selectionReason}
          </div>
        )}

        {/* Items that went out to quote — the snapshot copied off the requisition. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">Items quoted against</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr>{['Item', 'Category', 'Qty', 'Expected Rate'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {(rfq.items || []).map((item, i) => (
                  <tr key={i}>
                    <td className="px-4 py-3">
                      <div className="text-slate-800 dark:text-slate-200">{item.description || 'Item'}</div>
                      {item.specification && <div className="text-xs text-slate-400">{item.specification}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{item.materialCategory || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                      {item.quantity} {item.quantityUnit || ''}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                      {item.expectedRateMinor ? formatMinor(item.expectedRateMinor) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Side-by-side comparison — every ranking flag comes from the server. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">Supplier comparison</h2>
            {!locked && (rfq.quotes || []).length > 0 && (
              <button
                onClick={() => { setQuoteSupplierId(null); setShowQuote(true); }}
                className="text-xs font-semibold text-indigo-600 hover:underline"
              >
                Record / correct a quote
              </button>
            )}
          </div>
          <RfqComparisonTable
            comparison={comparison}
            scorecards={scorecards}
            onSelectSupplier={handleSelect}
            selectingSupplierId={selectingSupplierId}
            canSelect
            locked={locked}
          />
        </motion.div>

        {rfq.notes && (
          <div className="text-xs text-slate-500 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3">
            <span className="font-semibold text-slate-600 dark:text-slate-300">Notes to suppliers: </span>{rfq.notes}
          </div>
        )}
      </div>

      <RecordQuoteModal
        open={showQuote}
        rfq={rfq}
        initialSupplierId={quoteSupplierId}
        onClose={() => setShowQuote(false)}
        onSaved={result => {
          setShowQuote(false);
          applyResult(result);
          void loadScorecards(result.rfq);
        }}
      />
    </div>
  );
}

export default RfqDetailPage;
